import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Spinner,
  Button,
  Alert,
  ButtonGroup,
  Badge,
} from "reactstrap";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { fetchData } from "../../services/apiServices";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import "./calendar.css";

const CalendarView = () => {
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [calendarView, setCalendarView] = useState("month");
  const [dateFilter, setDateFilter] = useState("combined");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [alert, setAlert] = useState({
    visible: false,
    message: "",
    type: "",
  });
  const calendarRef = useRef(null);

  // Track if the component is mounted
  const isMounted = useRef(true);
  // To prevent multiple simultaneous API calls
  const isLoading = useRef(false);

  useEffect(() => {
    // Set up mount status
    isMounted.current = true;

    // Initial fetch
    fetchEvents();

    return () => {
      // Cleanup on unmount
      isMounted.current = false;
    };
  }, []);

  // Handle changes that require data refresh
  useEffect(() => {
    if (isMounted.current) {
      fetchEvents();
    }
  }, [calendarView, dateFilter]);

  // Handle currentDate changes (when user navigates calendar)
  useEffect(() => {
    if (isMounted.current) {
      fetchEvents();
    }
  }, [currentDate]);

  // Keyboard navigation: left/right arrows move calendar prev/next based on current view
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!calendarRef.current) return;
      const api = calendarRef.current.getApi();
      if (e.key === "ArrowLeft") {
        api.prev();
      } else if (e.key === "ArrowRight") {
        api.next();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [calendarView]);

  const fetchEvents = async () => {
    try {
      // Prevent additional calls if already loading
      if (isLoading.current) {
        console.log("Already fetching data, skipping request");
        return;
      }

      // Set both loading states
      setLoading(true);
      isLoading.current = true;

      console.log("Fetching calendar events:", {
        view: calendarView,
        date: currentDate,
        filter: dateFilter,
      });

      // Extract year, month, and day from currentDate
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1; // JavaScript months are 0-based
      const day = currentDate.getDate();

      // Prepare API parameters based on the current view
      const params = {
        view: calendarView,
        year,
        month,
        dateFilter,
        page: 1,
        limit: 50, // Fetch a reasonable number of events at once
      };

      // Add day parameter for week and day views
      if (calendarView === "week" || calendarView === "day") {
        params.day = day;
      }

      console.log("API request params:", params);

      const response = await fetchData("api/event/events-calendar", params);
      console.log("API response:", response);

      if (isMounted.current) {
        if (response && response.success) {
          const transformedEvents = transformEventsForCalendar(
            response.events || [],
            response.eventsByDate || {}
          );
          setEvents(transformedEvents);
          console.log("Processed events:", transformedEvents.length);
        } else {
          throw new Error(
            (response && response.message) || "Unknown error occurred"
          );
        }
      }
    } catch (error) {
      console.error("Error fetching events:", error);

      if (isMounted.current) {
        setAlert({
          visible: true,
          message: `Failed to load events: ${error.message || "Unknown error"}`,
          type: "danger",
        });
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
      // Always reset the loading ref
      isLoading.current = false;
    }
  };

  // Events are already filtered in transformEventsForCalendar based on dateFilter
  const displayedEvents = events;

  const transformEventsForCalendar = (eventsData = [], eventsByDate = {}) => {
    console.log("Transforming events:", {
      eventsCount: eventsData.length,
      dateCount: Object.keys(eventsByDate).length,
      dateFilter,
    });

    let calendarEvents = [];

    // Process events by date if available (preferred format from API)
    if (Object.keys(eventsByDate).length > 0) {
      Object.entries(eventsByDate).forEach(([date, dateEvents]) => {
        if (!Array.isArray(dateEvents)) {
          console.warn("Invalid events for date:", date, dateEvents);
          return;
        }

        dateEvents.forEach((event) => {
          if (!event) return;

          try {
            // For eventsByDate, the date key represents the relevant date based on dateFilter
            // So we use the date key directly for positioning on calendar
            const eventDate = new Date(date);

            // Determine event type and styling based on dateFilter
            let eventType, backgroundColor, title, classNames;

            if (dateFilter === "start_date") {
              eventType = "start";
              backgroundColor = "#556ee6";
              title = `${event.event_title || "Unnamed Event"}`;
              classNames = ["start-event"];
            } else if (dateFilter === "end_date") {
              eventType = "close";
              backgroundColor = "#f46a6a";
              title = `${event.event_title || "Unnamed Event"}`;
              classNames = ["close-event"];
            } else {
              // Combined view - determine based on which date this represents
              const listDate = new Date(event.list_date || event.calendarDate);
              const isStartDate =
                Math.abs(eventDate - listDate) < 24 * 60 * 60 * 1000; // within 1 day

              if (isStartDate) {
                eventType = "start";
                backgroundColor = "#556ee6";
                title = `${event.event_title || "Unnamed Event"}`;
                classNames = ["start-event"];
              } else {
                eventType = "close";
                backgroundColor = "#f46a6a";
                title = `${event.event_title || "Unnamed Event"}`;
                classNames = ["close-event"];
              }
            }

            calendarEvents.push({
              id: `${eventType}-${event._id || "unknown"}-${date}`,
              title,
              start: eventDate,
              backgroundColor,
              borderColor: backgroundColor,
              textColor: "#fff",
              eventType,
              originalEvent: event,
              classNames,
              extendedProps: {
                totalPoolInUsd: event.total_pool_in_usd || 0,
                eventImage: event.event_image || "",
                dollarVolume: event.total_pool_in_usd || 0,
                subMarkets: event.sub_markets?.length || 0,
                isLiveSports: event.is_live_sports || false,
                isBookmarked: event.isBookmarked || false,
              },
            });
          } catch (err) {
            console.error("Error processing event:", err, event);
          }
        });
      });
    } else {
      // Fallback: Process regular event list (legacy format)
      eventsData.forEach((event) => {
        if (!event) return;

        try {
          // Event start date
          const startDate =
            event.calendarDate || event.list_date || event.createdAt;
          if (
            startDate &&
            (dateFilter === "start_date" || dateFilter === "combined")
          ) {
            calendarEvents.push({
              id: `start-${event._id || "unknown"}`,
              title: `${event.event_title || "Unnamed Event"}`,
              start: new Date(startDate),
              classNames: ["start-event"],
              backgroundColor: "#556ee6",
              borderColor: "#556ee6",
              textColor: "#fff",
              eventType: "start",
              originalEvent: event,
              extendedProps: {
                totalPoolInUsd: event.total_pool_in_usd || 0,
                eventImage: event.event_image || "",
                dollarVolume: event.total_pool_in_usd || 0,
                subMarkets: event.sub_markets?.length || 0,
                isLiveSports: event.is_live_sports || false,
                isBookmarked: event.isBookmarked || false,
              },
            });
          }

          // Event end date if available
          if (
            (dateFilter === "end_date" || dateFilter === "combined") &&
            event.sub_markets &&
            Array.isArray(event.sub_markets) &&
            event.sub_markets.length > 0
          ) {
            const endDate = event.sub_markets[0].end_date;
            if (endDate) {
              calendarEvents.push({
                id: `close-${event._id || "unknown"}`,
                title: `${event.event_title || "Unnamed Event"}`,
                start: new Date(endDate),
                classNames: ["close-event"],
                backgroundColor: "#f46a6a",
                borderColor: "#f46a6a",
                textColor: "#fff",
                eventType: "close",
                originalEvent: event,
                extendedProps: {
                  totalPoolInUsd: event.total_pool_in_usd || 0,
                  eventImage: event.event_image || "",
                  dollarVolume: event.total_pool_in_usd || 0,
                  subMarkets: event.sub_markets?.length || 0,
                  isLiveSports: event.is_live_sports || false,
                  isBookmarked: event.isBookmarked || false,
                },
              });
            }
          }
        } catch (err) {
          console.error("Error processing event:", err);
        }
      });
    }

    console.log("Transformed calendar events:", calendarEvents.length);
    return calendarEvents;
  };

  const handleEventClick = (info) => {
    try {
      const eventId = info.event.extendedProps.originalEvent._id;
      if (eventId) {
        // Navigate to event detail page
        window.location.href = `/events/edit/${eventId}`;
      } else {
        console.warn("Event clicked but no ID found:", info.event);
      }
    } catch (err) {
      console.error("Error handling event click:", err);
    }
  };

  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
  };

  const handleViewChange = (viewName) => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();

      // Use proper capitalization for view names
      const viewMap = {
        month: "dayGridMonth",
        week: "dayGridWeek",
        day: "dayGridDay",
      };

      calendarApi.changeView(viewMap[viewName] || "dayGridMonth");
      setCalendarView(viewName);
    }
  };

  const handleDatesSet = (dateInfo) => {
    // Only update current date if year or month has changed
    const newDate = dateInfo.view.currentStart;

    if (
      newDate.getFullYear() !== currentDate.getFullYear() ||
      newDate.getMonth() !== currentDate.getMonth()
    ) {
      console.log("Date range changed:", newDate);
      setCurrentDate(newDate);
      // Don't call fetchEvents here as it will be triggered by the currentDate useEffect
    }
  };

  // Dismiss alerts
  const dismissAlert = () => {
    setAlert({ ...alert, visible: false });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <Breadcrumbs title="Events" breadcrumbItem="Calendar View" />
            <div>
              <ButtonGroup className="me-2 date-filter-buttons">
                <Button
                  color={calendarView === "month" ? "primary" : "secondary"}
                  onClick={() => handleViewChange("month")}
                  className="btn-sm"
                >
                  Month
                </Button>
                <Button
                  color={calendarView === "week" ? "primary" : "secondary"}
                  onClick={() => handleViewChange("week")}
                  className="btn-sm"
                >
                  Week
                </Button>
                <Button
                  color={calendarView === "day" ? "primary" : "secondary"}
                  onClick={() => handleViewChange("day")}
                  className="btn-sm"
                >
                  Day
                </Button>
              </ButtonGroup>
              <Button color="primary" onClick={fetchEvents} disabled={loading}>
                <i className="mdi mdi-refresh me-1"></i> Refresh
              </Button>
            </div>
          </div>

          {alert.visible && (
            <Alert color={alert.type} className="mb-4" toggle={dismissAlert}>
              {alert.message}
            </Alert>
          )}

          <Row>
            <Col>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between mb-4">
                    <ButtonGroup className="date-filter-buttons">
                      <Button
                        color={
                          dateFilter === "start_date" ? "primary" : "light"
                        }
                        onClick={() => handleDateFilterChange("start_date")}
                        size="sm"
                      >
                        Start Dates
                      </Button>
                      <Button
                        color={dateFilter === "end_date" ? "primary" : "light"}
                        onClick={() => handleDateFilterChange("end_date")}
                        size="sm"
                      >
                        End Dates
                      </Button>
                      <Button
                        color={dateFilter === "combined" ? "primary" : "light"}
                        onClick={() => handleDateFilterChange("combined")}
                        size="sm"
                      >
                        Combined
                      </Button>
                    </ButtonGroup>

                    <div className="text-end">
                      <Badge color="info" className="me-1">
                        Total Events: {events.length}
                      </Badge>
                    </div>
                  </div>

                  {/* calendar area without tabs */}
                  {loading ? (
                    <div className="text-center my-5">
                      <Spinner color="primary" />
                    </div>
                  ) : (
                    <div className="calendar-wrapper">
                      <FullCalendar
                        ref={calendarRef}
                        plugins={[dayGridPlugin, interactionPlugin]}
                        headerToolbar={{ left: "", center: "title", right: "" }}
                        initialView={
                          calendarView === "month"
                            ? "dayGridMonth"
                            : calendarView === "week"
                            ? "dayGridWeek"
                            : "dayGridDay"
                        }
                        events={displayedEvents}
                        eventClick={handleEventClick}
                        height="auto"
                        eventTimeFormat={{
                          hour: "2-digit",
                          minute: "2-digit",
                          meridiem: "short",
                        }}
                        datesSet={handleDatesSet}
                        windowResize={function (arg) {
                          // Adjust view based on screen size
                          if (window.innerWidth < 768) {
                            arg.view.calendar.changeView("dayGridDay");
                          }
                        }}
                        eventDidMount={(info) => {
                          // Add tooltip to event for more details
                          const event = info.event;
                          const eventTitle = event.title;
                          const dollarVolume =
                            event.extendedProps?.dollarVolume || 0;
                          const subMarkets =
                            event.extendedProps?.subMarkets || 0;
                          const isLiveSports =
                            event.extendedProps?.isLiveSports;
                          const isBookmarked =
                            event.extendedProps?.isBookmarked;
                          const eventType = event.eventType;

                          const tooltip = document.createElement("div");
                          tooltip.className = "event-tooltip";
                          tooltip.innerHTML = `
                            <strong>${eventTitle}</strong><br/>
                            <small>${
                              eventType === "start"
                                ? "Event Start"
                                : "Event End"
                            }</small><br/>
                            ${
                              dollarVolume > 0
                                ? `Total Pool: $${dollarVolume.toLocaleString()}<br/>`
                                : ""
                            }
                            ${
                              subMarkets > 0
                                ? `Sub-markets: ${subMarkets}<br/>`
                                : ""
                            }
                            ${
                              isLiveSports
                                ? '<span style="color: #28a745;">🔴 Live Sports</span><br/>'
                                : ""
                            }
                            ${
                              isBookmarked
                                ? '<span style="color: #ffc107;">⭐ Bookmarked</span><br/>'
                                : ""
                            }
                            <small>Click for details</small>
                          `;

                          info.el.addEventListener("mouseover", function () {
                            document.body.appendChild(tooltip);
                            const rect = info.el.getBoundingClientRect();
                            tooltip.style.position = "absolute";
                            tooltip.style.top =
                              rect.bottom + window.scrollY + "px";
                            tooltip.style.left =
                              rect.left + window.scrollX + "px";
                            tooltip.style.backgroundColor = "white";
                            tooltip.style.border = "1px solid #ccc";
                            tooltip.style.padding = "8px";
                            tooltip.style.borderRadius = "4px";
                            tooltip.style.boxShadow =
                              "0 2px 8px rgba(0,0,0,0.15)";
                            tooltip.style.zIndex = 10000;
                            tooltip.style.fontSize = "12px";
                            tooltip.style.maxWidth = "200px";
                          });

                          info.el.addEventListener("mouseout", function () {
                            if (document.body.contains(tooltip)) {
                              document.body.removeChild(tooltip);
                            }
                          });
                        }}
                      />
                    </div>
                  )}

                  {/* legend and info below calendar */}
                  <div className="mt-4">
                    <div className="calendar-legend d-flex flex-wrap">
                      <div className="me-3 mb-2">
                        <span
                          className="legend-box"
                          style={{ backgroundColor: "#556ee6" }}
                        ></span>
                        <span>Event Start Date</span>
                      </div>
                      <div className="me-3 mb-2">
                        <span
                          className="legend-box"
                          style={{ backgroundColor: "#f46a6a" }}
                        ></span>
                        <span>Event Close Date</span>
                      </div>
                    </div>
                    <p className="text-muted mt-2">
                      <small>
                        <i className="mdi mdi-information-outline me-1"></i>{" "}
                        Click on any event to view its details
                      </small>
                    </p>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default CalendarView;
